/**
 * Field visibility rules based on trigger filtering context.
 *
 * Mirrors backend logic in `backend/logiblocks/services/node_defaults/path_node.py`.
 * Determines which fields should be hidden in the LHS conditional variable picker
 * based on what the trigger node has already constrained.
 */

import type { TriggerFilterContext } from './triggerFilterUtils';

/**
 * Determines if a field should be hidden from the LHS variable picker.
 *
 * Rules mirror the backend FieldConfig `visible` lambdas in path_node.py:
 *
 * - Alert Trigger:
 *   - Hide `alert_type` when only 1 alert type selected
 *   - Hide `severity` when only 1 alert type selected
 *   - Hide `alert_stage` when not multi-phase
 *
 * - Location Trigger:
 *   - Hide `location` when location is already set
 *
 * - ETA Change Trigger:
 *   - Hide `delta` when change_by is already set
 *
 * - Status Progress Trigger:
 *   - Hide `status` when only 1 status selected
 *
 * - Inbound Trigger:
 *   - Hide `event_type` when event type is already set
 *
 * - Manual Execution Trigger:
 *   - Hide `external_trigger` when external trigger is already set
 *
 * @param fieldName - The field name (e.g., "alert_type", "location")
 * @param nodeId - The node ID that publishes this variable
 * @param triggerCtx - The trigger filtering context
 * @returns true if the field should be hidden, false otherwise
 */
export function shouldHideField(
  fieldName: string,
  nodeId: string,
  triggerCtx: TriggerFilterContext | null
): boolean {
  if (!triggerCtx) {
    return false;
  }

  // Only apply filtering to trigger node variables
  // System variables and other ancestor variables are not affected
  // Case-insensitive check since node IDs can be "alertTrigger:..." or "alert_trigger:..."
  const isTriggerVariable =
    nodeId !== 'system' && nodeId.toLowerCase().includes('trigger');

  if (!isTriggerVariable) {
    return false;
  }

  switch (triggerCtx.triggerType) {
    case 'alert_trigger':
      return shouldHideAlertTriggerField(fieldName, triggerCtx);

    case 'location_trigger':
      return shouldHideLocationTriggerField(fieldName, triggerCtx);

    case 'eta_change_trigger':
      return shouldHideEtaTriggerField(fieldName, triggerCtx);

    case 'status_progress_trigger':
      return shouldHideStatusProgressTriggerField(fieldName, triggerCtx);

    case 'inbound_trigger':
      return shouldHideInboundTriggerField(fieldName, triggerCtx);

    case 'manual_execution_trigger':
      return shouldHideManualExecutionTriggerField(fieldName, triggerCtx);

    default:
      return false;
  }
}

// --- Trigger-specific visibility rules ---

function shouldHideAlertTriggerField(
  fieldName: string,
  ctx: TriggerFilterContext
): boolean {
  const alertTypes = ctx.alertTypes || [];
  const isAllTypes = ctx.isAllAlertTypes || false;
  const coveredPhases = ctx.alertCoveredPhases || new Set();

  switch (fieldName) {
    case 'alert_type':
      // Visible when: is_all_types OR len(alert_types) != 1
      // Hidden when: NOT is_all_types AND len(alert_types) == 1
      return !isAllTypes && alertTypes.length === 1;

    case 'alert_stage':
      // Visible when: is_all_types OR (len(alert_types) > 1 AND len(covered_phases) > 1)
      // Hidden otherwise
      return !(isAllTypes || (alertTypes.length > 1 && coveredPhases.size > 1));

    case 'severity':
      // Visible when: is_all_types OR len(alert_types) != 1
      // Hidden when: NOT is_all_types AND len(alert_types) == 1
      return !isAllTypes && alertTypes.length === 1;

    // alert_state, created_at, threshold_value, current_value are always visible
    default:
      return false;
  }
}

function shouldHideLocationTriggerField(
  fieldName: string,
  ctx: TriggerFilterContext
): boolean {
  switch (fieldName) {
    case 'location':
      // Hidden when: locationId is already set
      return !!ctx.locationId;

    // arrival_time is always visible
    default:
      return false;
  }
}

function shouldHideEtaTriggerField(
  fieldName: string,
  ctx: TriggerFilterContext
): boolean {
  switch (fieldName) {
    case 'delta':
      // Hidden when: change_by is already set
      return !!ctx.etaChangeBy;

    // direction, updated_at are always visible
    default:
      return false;
  }
}

function shouldHideStatusProgressTriggerField(
  fieldName: string,
  ctx: TriggerFilterContext
): boolean {
  const selectedValues = ctx.statusProgressValues || [];

  switch (fieldName) {
    case 'status':
      // Visible when: len(selected_values) != 1
      // Hidden when: len(selected_values) == 1
      return selectedValues.length === 1;

    // updated_at is always visible
    default:
      return false;
  }
}

function shouldHideInboundTriggerField(
  fieldName: string,
  ctx: TriggerFilterContext
): boolean {
  switch (fieldName) {
    case 'event_type':
      // Hidden when: event_type is already set
      return !!ctx.inboundEventType;

    // sender, received_at are always visible
    default:
      return false;
  }
}

function shouldHideManualExecutionTriggerField(
  fieldName: string,
  ctx: TriggerFilterContext
): boolean {
  switch (fieldName) {
    case 'external_trigger':
      // Hidden when: external_trigger is already set
      return !!ctx.externalTrigger;

    // triggered_by, triggered_at are always visible
    default:
      return false;
  }
}

/**
 * Gets a count of how many fields will be hidden for a given node.
 * Useful for debugging and testing.
 */
export function getHiddenFieldCount(
  fields: string[],
  nodeId: string,
  triggerCtx: TriggerFilterContext | null
): number {
  return fields.filter((f) => shouldHideField(f, nodeId, triggerCtx)).length;
}
